import { getBannerInfo } from "./bannerInfo";
import React, { useEffect, useState } from "react";
import { collection, getDocs, onSnapshot, query, where } from "firebase/firestore";
import { DateTime } from "luxon";
import { db } from "../firebaseConfig";
import { useAuth } from "../context/AuthContext";
import "./MySchedule.css";

const ZONE = "America/New_York";
const normalize = value => String(value || "").trim().toLowerCase();

// Existing records contain both ISO timestamps and local HH:mm values.
export function shootTime(value, date) {
  if (!value) return null;
  const text = String(value);
  const parsed = /^\d{2}:\d{2}(:\d{2})?$/.test(text)
    ? DateTime.fromISO(`${date}T${text}`, { zone: ZONE })
    : DateTime.fromISO(text, { zone: ZONE });
  return parsed.isValid ? parsed.setZone(ZONE) : null;
}

function Field({ label, value }) {
  return value ? <div className="my-schedule-field"><h3>{label}</h3><p>{value}</p></div> : null;
}

export default function MySchedule() {
  const { currentUser } = useAuth();
  const [staff, setStaff] = useState([]);
  const [member, setMember] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [tab, setTab] = useState("today");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [now, setNow] = useState(() => DateTime.now().setZone(ZONE));

  useEffect(() => {
    const timer = setInterval(() => setNow(DateTime.now().setZone(ZONE)), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    let active = true;
    let unsubscribe;
    setLoading(true);
    setError("");
    setMember(null);
    setStaff([]);
    setAppointments([]);
    if (!currentUser) { setLoading(false); return; }
    async function load() {
      try {
        const snapshot = await getDocs(collection(db, "staff"));
        if (!active) return;
        const people = snapshot.docs.map(record => ({ ...record.data(), id: record.id }));
        const matches = people.filter(person => normalize(person.email) === normalize(currentUser.email));
        let profileError = "";
        if (!currentUser.email) {
          profileError = "Your login has no email address. Please contact scheduling.";
        } else if (matches.length === 0) {
          profileError = `No staff record matches your login email (${currentUser.email}). Ask scheduling to update the email on your existing staff record to match this login.`;
        } else if (matches.length > 1) {
          profileError = `More than one staff record uses your login email (${currentUser.email}). Ask scheduling to resolve the duplicate staff records.`;
        } else if (!normalize(matches[0].nickname)) {
          profileError = "Your staff record matches your login, but its photographer nickname is blank. Ask scheduling to add your assignment nickname.";
        } else if (people.filter(person => person.nickname === matches[0].nickname).length !== 1) {
          profileError = `Your staff nickname (${matches[0].nickname}) appears on more than one staff record. Ask scheduling to resolve the duplicate nickname before loading your assignments.`;
        }
        if (profileError) {
          setError(profileError);
          setLoading(false);
          return;
        }
        setStaff(people);
        setMember(matches[0]);
        unsubscribe = onSnapshot(query(collection(db, "appointments"),
          where("photographers", "array-contains", matches[0].nickname)), result => {
          if (!active) return;
          setAppointments(result.docs.map(record => ({ ...record.data(), id: record.id })));
          setLoading(false);
          setError("");
        }, () => {
          if (!active) return;
          setAppointments([]);
          setError("We couldn't load your schedule. Please reload the page or contact scheduling.");
          setLoading(false);
        });
      } catch {
        if (active) {
          setError("We couldn't load your staff profile. Please reload the page or contact scheduling.");
          setLoading(false);
        }
      }
    }
    load();
    return () => { active = false; if (unsubscribe) unsubscribe(); };
  }, [currentUser]);

  const today = now.toISODate();
  const shoots = appointments.map(appointment => {
    const day = DateTime.fromISO(appointment.appointmentDate || "", { zone: ZONE }).setZone(ZONE);
    return { ...appointment, day, start: shootTime(appointment.startTime, day.toISODate()) || day,
      finish: shootTime(appointment.endTime || appointment.endDate, day.toISODate()) };
  }).filter(appointment => appointment.day.isValid &&
    (tab === "today" ? appointment.day.toISODate() === today : appointment.day.toISODate() > today))
    .sort((a, b) => a.start.toMillis() - b.start.toMillis());

  return <main className="my-schedule">
    <header><p className="my-schedule-eyebrow">YOUR ASSIGNMENTS</p><h1>My Schedule</h1>
      <p>{member ? `${member.name || member.nickname} · ` : ""}{now.toFormat("cccc, LLLL d")} · Eastern time</p>
    </header>
    <div className="my-schedule-tabs" role="group" aria-label="Schedule dates">
      <button aria-pressed={tab === "today"} onClick={() => setTab("today")}>Today</button>
      <button aria-pressed={tab === "upcoming"} onClick={() => setTab("upcoming")}>Upcoming</button>
    </div>
    {loading ? <p role="status">Loading your assignments…</p> : error ? <p role="alert">{error}</p> :
      <div aria-live="polite">
        <p>{shoots.length} {shoots.length === 1 ? "shoot" : "shoots"}{tab === "today" ? " today" : " after today"}</p>
        {!shoots.length && <div className="my-schedule-empty">{tab === "today" ? "No shoots assigned today." : "No upcoming shoots assigned."}</div>}
        {shoots.map(shoot => <article className="my-schedule-card" key={shoot.id}>
          <p className="my-schedule-date">{shoot.day.toFormat("cccc, LLL d")} · {shoot.start.toFormat("h:mm a")}{shoot.finish ? ` – ${shoot.finish.toFormat("h:mm a")}` : ""}</p>
          <h2>{shoot.client || "Shoot"}</h2>
          {normalize(shoot.color) === "black" || normalize(shoot.color) === "#000000" ? <p className="my-schedule-canceled">Canceled</p> : null}
          <Field label="Summary" value={shoot.summary} />
          <Field label="Address" value={shoot.address} />
          <Field label="Field / building" value={shoot.fieldBuildingLocation} />
          {shoot.address && <a className="my-schedule-action" href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(shoot.address)}`} target="_blank" rel="noopener noreferrer">Get directions</a>}
          <details><summary>Shoot details & crew</summary>
            <Field label="Notes & schedule" value={shoot.notes} />
            <Field label="Extra equipment" value={shoot.extraEquipment || "None listed"} />
            <Field label="Photographer Roles" value={shoot.bellNotes || "None listed"} />
            <Field label="Banner Info" value={getBannerInfo(shoot) || "None listed"} />
            <h3>Your crew</h3>
            <ul className="my-schedule-crew">{(shoot.photographers || []).filter(nickname => nickname !== member.nickname && nickname !== "Unassigned").map(nickname => {
              const matches = staff.filter(person => person.nickname === nickname);
              const colleague = matches.length === 1 ? matches[0] : null;
              const phone = String(colleague?.phone || "").replace(/[^\d+]/g, "");
              const callable = /^\+?\d{7,15}$/.test(phone);
              return <li key={nickname}><span>{colleague?.name || nickname}</span>{callable
                ? <a className="my-schedule-action" href={`tel:${phone}`}>Call {nickname}</a>
                : <span className="my-schedule-muted">Phone unavailable</span>}</li>;
            })}</ul>
            {!(shoot.photographers || []).some(nickname => nickname !== member.nickname && nickname !== "Unassigned") && <p>No other photographers assigned.</p>}
          </details>
        </article>)}
      </div>}
  </main>;
}
